/**
 * Product compare table: product/variant images from Liquid, spec rows from compare JSON.
 */

class ProductCompareModels extends HTMLElement {
  static instances = 0;

  #abortController;
  #stickyObserver;
  #specsObserver;
  // Where the pinned bar sits (header plus any pinned bar above it) and how tall it is when stuck.
  #pinOffset = 0;
  #stuckBarHeight = 0;
  #cardsPassed = false;
  #specsPassed = false;
  #settleTimer = 0;
  #requestVersion = 0;
  #columns = [];
  // Product indexes whose compare key is missing from the loaded worksheet.
  #unmatched = new Set();
  #visibleIndexes = [];
  #sheet = null;
  #tabletQuery;
  #desktopQuery;
  #amountObservers = [];
  #resizeFrame = 0;
  #wrapFrame = 0;
  #viewportWidth = 0;
  #headerVisible = true;
  // Provider widgets need unique ids; sections can appear more than once per page.
  #instanceId = `${(ProductCompareModels.instances += 1)}`;

  connectedCallback() {
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    this.#tabletQuery = window.matchMedia('(min-width: 768px)');
    this.#desktopQuery = window.matchMedia('(min-width: 1280px)');
    this.#columns = [];
    this.querySelectorAll('[data-product-index]').forEach((column) => {
      const index = Number(column.dataset.productIndex);
      if (Number.isInteger(index) && index >= 0) this.#columns[index] = column;
    });

    this.#applyVariantQuery();
    this.#ensureVisibleIndexes();

    this.addEventListener('click', this.#onClick, { signal });
    this.addEventListener('toggle', this.#onToggle, { capture: true, signal });
    this.addEventListener('keydown', this.#onKeyDown, { signal });
    this.addEventListener('shopify:block:select', this.#onBlockSelect, { signal });
    document.addEventListener('pointerdown', this.#onPointerDown, { signal });
    this.#filterInput?.addEventListener('change', this.#onFilterChange, { signal });
    this.#tabletQuery.addEventListener('change', this.#onBreakpointChange, { signal });
    window.addEventListener('resize', this.#onResize, { signal });
    window.addEventListener('scroll', this.#onScroll, { passive: true, signal });
    document.addEventListener('header:visibility-change', this.#onHeaderVisibilityChange, { signal });

    this.#viewportWidth = window.innerWidth;
    this.#observeSticky();
    this.#sync();
    this.dataset.jsReady = 'true';
    this.#loadSheet();
    // Barlow can finish loading after the first measurement and change wraps.
    document.fonts?.ready.then(() => {
      if (!signal.aborted) this.#queueWrapCheck();
    });
  }

  disconnectedCallback() {
    this.#requestVersion += 1;
    this.#stickyObserver?.disconnect();
    this.#stickyObserver = null;
    this.#specsObserver?.disconnect();
    this.#specsObserver = null;
    clearTimeout(this.#settleTimer);
    this.#settleTimer = 0;
    this.#disconnectAmountObservers();
    if (this.#resizeFrame) cancelAnimationFrame(this.#resizeFrame);
    this.#resizeFrame = 0;
    if (this.#wrapFrame) cancelAnimationFrame(this.#wrapFrame);
    this.#wrapFrame = 0;
    this.#clearStuck();
    this.#abortController?.abort();
  }

  get #filterInput() {
    return this.querySelector('[data-filter-differences]');
  }

  get #statusEl() {
    return this.querySelector('[data-compare-status]');
  }

  get #specsEl() {
    return this.querySelector('[data-compare-specs]');
  }

  get #columnsEl() {
    return this.querySelector('[data-compare-columns]');
  }

  get #toolbarEl() {
    return this.querySelector('[data-sticky-header]');
  }

  get #cardsEl() {
    return this.querySelector('[data-compare-cards]');
  }

  get #maxVisible() {
    const total = this.#columns.filter((column, index) => this.#isAvailable(index)).length;
    const cap = this.#tabletQuery?.matches
      ? this.#readCount(this.dataset.columnsDesktop, 3)
      : this.#readCount(this.dataset.columnsMobile, 2);
    return Math.min(cap, total);
  }

  /**
   * The first column stays fixed on tablet/desktop only when the section opts
   * in, e.g. a product page comparing the current product against others.
   * @returns {boolean}
   */
  get #lockFirst() {
    return this.dataset.lockFirst !== 'false' && Boolean(this.#tabletQuery?.matches);
  }

  /**
   * Group titles follow the section heading level set in Liquid.
   * @returns {string}
   */
  get #groupHeadingTag() {
    const tag = this.dataset.groupHeadingTag;
    return /^h[2-6]$/.test(tag || '') ? tag : 'h3';
  }

  /**
   * Like the original compare sections, a model whose compare key is not in
   * the worksheet is left out entirely: no column and no picker option.
   * Before the worksheet loads every rendered model counts as available.
   * @param {number} index
   * @returns {boolean}
   */
  #isAvailable(index) {
    return Boolean(this.#columns[index]) && !this.#unmatched.has(index);
  }

  #readCount(value, fallback) {
    const count = Number(value);
    return Number.isInteger(count) && count > 0 ? count : fallback;
  }

  #applyVariantQuery() {
    const raw = new URLSearchParams(window.location.search).get('variants');
    if (!raw) return;

    const requested = raw.split(',').map((value) => value.trim()).filter(Boolean);
    if (!requested.length) return;

    const matched = [];
    requested.forEach((variantId) => {
      const index = this.#columns.findIndex((column) => column?.dataset.variantId === variantId);
      if (index !== -1 && !matched.includes(index)) matched.push(index);
    });

    if (matched.length) this.#visibleIndexes = matched;
  }

  #ensureVisibleIndexes() {
    const available = this.#columns
      .map((column, index) => (this.#isAvailable(index) ? index : -1))
      .filter((index) => index >= 0);
    const maxVisible = this.#maxVisible;

    this.#visibleIndexes = this.#visibleIndexes.filter((index) => available.includes(index));

    for (const index of available) {
      if (this.#visibleIndexes.length >= maxVisible) break;
      if (!this.#visibleIndexes.includes(index)) this.#visibleIndexes.push(index);
    }

    if (this.#visibleIndexes.length > maxVisible) {
      this.#visibleIndexes = this.#visibleIndexes.slice(0, maxVisible);
    }
  }

  #sync() {
    this.#ensureVisibleIndexes();
    this.style.setProperty('--product-compare-visible', String(this.#visibleIndexes.length || 1));
    // A lone product has nothing beside it; the stylesheet centers its card and specs.
    this.toggleAttribute('data-single', this.#visibleIndexes.length === 1);
    this.#syncColumns();
    this.#syncPickers();
    this.#renderSpecs();
    this.#applyFilter();
  }

  #syncColumns() {
    const container = this.#columnsEl;
    if (!container) return;

    this.#visibleIndexes.forEach((index) => {
      const column = this.#columns[index];
      if (!column) return;
      column.hidden = false;
      container.append(column);
    });

    this.#columns.forEach((column, index) => {
      if (!column || this.#visibleIndexes.includes(index)) return;
      column.hidden = true;
      container.append(column);
    });
  }

  #syncPickers() {
    this.#visibleIndexes.forEach((productIndex, slot) => {
      const column = this.#columns[productIndex];
      const picker = column?.querySelector('[data-product-picker]');
      const menu = column?.querySelector('[data-picker-menu]');
      if (!picker || !menu) return;

      const canPick = this.#canPick(slot);
      picker.toggleAttribute('data-picker-disabled', !canPick);
      if (!canPick) picker.open = false;

      const summary = picker.querySelector('summary');
      const selectLabel = this.dataset.selectProductLabel || '';
      const productTitle = column.dataset.productTitle || '';
      if (summary) {
        if (canPick && selectLabel) {
          summary.setAttribute('aria-label', `${productTitle}. ${selectLabel}`);
        } else {
          summary.removeAttribute('aria-label');
        }
      }

      menu.replaceChildren();
      if (!canPick) return;

      // Every model is listed in block order; models already shown in a slot
      // are disabled, so a pick always brings in a model that is not visible.
      this.#columns.forEach((optionColumn, optionIndex) => {
        if (!this.#isAvailable(optionIndex)) return;

        const item = document.createElement('li');
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'product-compare-models__option';
        button.dataset.selectProduct = String(optionIndex);
        button.disabled = this.#visibleIndexes.includes(optionIndex);
        button.textContent = optionColumn.dataset.productTitle || '';
        item.append(button);
        menu.append(item);
      });
    });
  }

  /**
   * A slot can change only when a model is left out of the visible slots.
   * When every model is already shown the picker is disabled, and a locked
   * first column never changes.
   * @param {number} slot
   * @returns {boolean}
   */
  #canPick(slot) {
    if (this.#lockFirst && slot === 0) return false;
    return this.#columns.some((column, index) => this.#isAvailable(index) && !this.#visibleIndexes.includes(index));
  }

  async #loadSheet() {
    const version = ++this.#requestVersion;
    const sheetName = this.dataset.sheet?.trim();
    const specsEl = this.#specsEl;

    if (!sheetName || typeof window.loadCompareData !== 'function') {
      this.#setStatus(this.dataset.unavailable || '');
      return;
    }

    this.#setStatus('');
    this.setAttribute('aria-busy', 'true');

    try {
      const sheet = await window.loadCompareData(sheetName);
      if (!this.isConnected || version !== this.#requestVersion) return;
      this.#sheet = sheet;
      this.#markUnmatched(sheet);
      const hasModels = this.#columns.some((column, index) => this.#isAvailable(index));
      this.#setStatus(hasModels ? '' : this.dataset.unavailable || '');
      this.#sync();
    } catch (error) {
      if (!this.isConnected || version !== this.#requestVersion) return;
      this.#sheet = null;
      if (specsEl) specsEl.replaceChildren();
      this.#setStatus(this.dataset.unavailable || '');
      console.error('Compare data could not be loaded.', error);
    } finally {
      if (this.isConnected && version === this.#requestVersion) {
        this.removeAttribute('aria-busy');
      }
    }
  }

  /**
   * @param {{ transformedData?: Array<{ key?: string }> }} sheet
   */
  #markUnmatched(sheet) {
    const keys = new Set(
      (Array.isArray(sheet?.transformedData) ? sheet.transformedData : []).map((row) => row?.key)
    );
    this.#unmatched = new Set();
    this.#columns.forEach((column, index) => {
      if (column && !keys.has(column.dataset.productKey)) this.#unmatched.add(index);
    });
  }

  #renderSpecs() {
    const container = this.#specsEl;
    if (!container) return;
    container.replaceChildren();
    if (!this.#sheet) return;

    const visibleColumns = this.#visibleIndexes.map((index) => this.#columns[index]).filter(Boolean);
    if (!visibleColumns.length) return;

    const rowsByKey = new Map(
      (Array.isArray(this.#sheet.transformedData) ? this.#sheet.transformedData : []).map((row) => [
        row.key,
        row,
      ])
    );

    if (this.dataset.enableStartingPrice !== 'false') {
      const startingPriceGroup = this.#buildStartingPriceGroup(visibleColumns);
      if (startingPriceGroup) container.append(startingPriceGroup);
    }

    Object.entries(this.#sheet.transformedGroup || {}).forEach(([rawTitle, fields]) => {
      const group = this.#buildGroup(rawTitle.replace(/\s+/g, ' ').trim(), fields, visibleColumns, rowsByKey);
      if (group) container.append(group);
    });
  }

  /**
   * The product page owns the financing configuration (which providers are on,
   * and which one is the entry channel). Compare must not duplicate it.
   * @returns {Element|null}
   */
  get #financingComponent() {
    const element = document.querySelector('financing-options');
    return element && typeof element.createMessageFor === 'function' ? element : null;
  }

  #buildStartingPriceGroup(visibleColumns) {
    const financing = this.#financingComponent;
    if (!financing?.entryChannel) return null;

    this.#disconnectAmountObservers();

    const group = document.createElement('section');
    group.className = 'product-compare-models__group';
    group.dataset.specGroup = '';

    const title = document.createElement(this.#groupHeadingTag);
    title.className = 'product-compare-models__group-title';
    title.textContent = this.dataset.startingPriceLabel || '';
    group.append(title);

    const row = document.createElement('div');
    row.className = 'product-compare-models__row';
    row.dataset.specRow = '';
    row.dataset.skipFilter = '';

    visibleColumns.forEach((column, slot) => {
      const cell = document.createElement('div');
      cell.className = 'product-compare-models__cell';
      const host = document.createElement('div');
      host.className = 'product-compare-models__starting-host';
      host.dataset.specValue = '';
      const template = column.querySelector('[data-starting-price]');
      if (template instanceof HTMLTemplateElement) {
        host.append(template.content.cloneNode(true));
      }

      const messageSlot = host.querySelector('[data-financing-message]');
      const message = financing.createMessageFor(
        Number(column.dataset.priceCents),
        `compare-financing-${this.#instanceId}-${slot}`
      );
      if (messageSlot && message) {
        messageSlot.append(message);
        this.#watchFinancingAmount(messageSlot, host.querySelector('[data-financing-amount]'));
      }

      cell.append(host);
      row.append(cell);
    });

    group.append(row);
    this.#refreshFinancingMessaging(group);
    return group;
  }

  /**
   * Providers render their copy asynchronously, so the headline amount is
   * mirrored from the message text once the widget paints and whenever the
   * amount changes.
   * @param {Element|null} source - Node the provider renders into.
   * @param {Element|null} target - Headline node.
   */
  #watchFinancingAmount(source, target) {
    if (!source || !target) return;

    const sync = () => {
      const match = (source.textContent || '').match(/([$€£¥]\s?\d[\d.,]*)\s*\/\s*mo\b/i);
      if (!match) {
        target.textContent = '';
        target.hidden = true;
        return;
      }
      target.textContent = `${match[1].replace(/\s+/g, '')}/mo`;
      target.hidden = false;
    };

    sync();
    const observer = new MutationObserver(sync);
    observer.observe(source, { childList: true, subtree: true, characterData: true });
    this.#amountObservers.push(observer);
  }

  #disconnectAmountObservers() {
    this.#amountObservers.forEach((observer) => observer.disconnect());
    this.#amountObservers = [];
  }

  /**
   * Provider widgets are injected after first paint, so the owning financing
   * component must refresh them against the amounts rendered here.
   * @param {ParentNode|null} root
   */
  #refreshFinancingMessaging(root) {
    const financing = this.#financingComponent;
    if (!financing || typeof financing.refreshMessagingIn !== 'function') return;
    requestAnimationFrame(() => financing.refreshMessagingIn(root));
  }

  /**
   * Open the same financing UI the product page would open for this variant.
   * The modal renders whichever providers that storefront has configured, so a
   * single-provider store shows only that provider's terms.
   * @param {Element} trigger
   */
  #openFinancing(trigger) {
    const cell = trigger.closest('.product-compare-models__cell');
    const row = trigger.closest('[data-spec-row]');
    const slot = row && cell ? Array.from(row.children).indexOf(cell) : -1;
    const column = slot >= 0 ? this.#columns[this.#visibleIndexes[slot]] : null;
    const priceCents = Number(column?.dataset.priceCents);

    const financing = document.querySelector('financing-options');
    if (financing && typeof financing.openForAmount === 'function') {
      financing.openForAmount(priceCents);
      return;
    }
    if (financing && typeof financing.openModal === 'function') {
      financing.openModal();
      return;
    }

    const providerLink = cell?.querySelector('.affirm-as-low-as a, [data-pp-message] a, klarna-placement a');
    if (providerLink instanceof HTMLElement) {
      providerLink.click();
    }
  }

  #buildGroup(title, fields, visibleColumns, rowsByKey) {
    if (!Array.isArray(fields) || !fields.length) return null;

    const group = document.createElement('section');
    group.className = 'product-compare-models__group';
    group.dataset.specGroup = '';

    if (title) {
      const heading = document.createElement(this.#groupHeadingTag);
      heading.className = 'product-compare-models__group-title';
      heading.textContent = String(title).trim();
      group.append(heading);
    }

    let hasRows = false;

    fields.forEach((field) => {
      const key = field?.key;
      if (key == null || key === '') return;

      const values = visibleColumns.map((column) => {
        const row = rowsByKey.get(column.dataset.productKey);
        return row ? row[key] : '';
      });

      if (values.every((value) => this.#isEmptyValue(value))) return;

      hasRows = true;
      const isCopy = this.#normalizeText(field.title) === '';
      const row = document.createElement('div');
      row.className = 'product-compare-models__row';
      row.dataset.specRow = '';
      row.dataset.specKey = String(key);
      if (isCopy) row.dataset.copy = '';

      const parts = values.map((value) => {
        const html = this.#formatValue(value);
        return isCopy ? { value: html, note: '' } : this.#splitFootnote(html);
      });

      parts.forEach(({ value, note }) => {
        const cell = document.createElement('div');
        cell.className = 'product-compare-models__cell';

        const valueEl = document.createElement('div');
        valueEl.className = 'product-compare-models__value';
        valueEl.dataset.specValue = '';
        valueEl.innerHTML = value;
        cell.append(valueEl);

        if (!isCopy) {
          const label = document.createElement('p');
          label.className = 'product-compare-models__label';
          label.textContent = String(field.title).trim();
          cell.append(label);
        }

        if (note) {
          const noteEl = document.createElement('div');
          noteEl.className = 'product-compare-models__note';
          noteEl.dataset.specNote = '';
          noteEl.innerHTML = note;
          cell.append(noteEl);
        }

        row.append(cell);
      });

      group.append(row);
    });

    return hasRows ? group : null;
  }

  #applyFilter() {
    const enabled = Boolean(this.#filterInput?.checked);

    this.querySelectorAll('[data-spec-row]').forEach((row) => {
      if (row.hasAttribute('data-skip-filter')) return;
      const values = Array.from(row.querySelectorAll('[data-spec-value]')).map((valueEl) => {
        const note = valueEl.parentElement?.querySelector('[data-spec-note]');
        return this.#normalizeText(valueEl.innerHTML + (note ? ` ${note.innerHTML}` : ''));
      });
      const hasValue = values.some((value) => !this.#isEmptyValue(value));
      // Spacing alone is not a spec difference: worksheets mix "6.1 Ah" and
      // "6.1Ah", so compare with every whitespace character removed.
      const comparable = values.map((value) => (this.#isEmptyValue(value) ? '' : value.replace(/\s+/g, '')));
      const allEqual = comparable.length > 1 && comparable.every((value) => value === comparable[0]);
      row.hidden = !hasValue || (enabled && allEqual);
    });

    this.querySelectorAll('[data-spec-group]').forEach((group) => {
      const rows = Array.from(group.querySelectorAll('[data-spec-row]'));
      group.hidden = rows.length > 0 && rows.every((row) => row.hidden);
    });

    this.#queueWrapCheck();
  }

  #queueWrapCheck() {
    if (this.#wrapFrame) cancelAnimationFrame(this.#wrapFrame);
    this.#wrapFrame = requestAnimationFrame(() => {
      this.#wrapFrame = 0;
      this.#syncWrappedRows();
    });
  }

  /**
   * A stat that wraps steps the whole row down to the smaller size (the
   * design's two stat sizes); one wrapped cell keeps its siblings at the same
   * size. Only mobile and desktop define a smaller size; tablet keeps one.
   * Rows are measured at their default size, so every mark is cleared first,
   * then all reads happen before any write.
   */
  #syncWrappedRows() {
    const rows = Array.from(this.querySelectorAll('[data-spec-row]:not([data-copy]):not([data-skip-filter])'));
    rows.forEach((row) => row.removeAttribute('data-wrapped'));
    if (this.#tabletQuery?.matches && !this.#desktopQuery?.matches) return;

    const wrapped = rows.filter((row) => {
      if (row.hidden) return false;
      return Array.from(row.querySelectorAll('[data-spec-value]')).some((valueEl) => {
        const lineHeight = parseFloat(getComputedStyle(valueEl).lineHeight);
        if (!(lineHeight > 0)) return false;
        return valueEl.getBoundingClientRect().height > lineHeight * 1.5;
      });
    });

    wrapped.forEach((row) => row.setAttribute('data-wrapped', ''));
  }

  #formatValue(value) {
    // A missing spec still needs a visible cell so columns stay aligned; the
    // dash is treated as empty again by #isEmptyValue when filtering rows.
    if (this.#isEmptyValue(value)) return '-';

    // Worksheet values arrive as markup such as `<div><br>230 ft<br>…<br></div>`.
    // Those edge `<br>` elements and blank wrappers render as empty lines, so
    // trim them on the parsed markup instead of on the raw string.
    const holder = document.createElement('div');
    holder.innerHTML = String(value).replace(/\n/g, '<br>');

    // Deepest-first: a wrapper can only be judged blank once its own children
    // have been trimmed away.
    Array.from(holder.querySelectorAll('*'))
      .reverse()
      .forEach((node) => {
        if (node.tagName === 'BR') return;
        this.#trimMarkupEdges(node);
        if (node.textContent.trim() === '' && !node.querySelector('img, svg, picture')) {
          node.remove();
        }
      });

    this.#trimMarkupEdges(holder);
    this.#collapseLineBreaks(holder);
    return holder.innerHTML;
  }

  /**
   * Worksheet cells append footnotes to the stat, e.g. `230 ft<br>*Detection
   * range: …`. The design shows the footnote as small copy under the label, so
   * everything after the first line break that starts with `*` becomes a note.
   * @param {string} html Output of #formatValue.
   * @returns {{ value: string, note: string }}
   */
  #splitFootnote(html) {
    const holder = document.createElement('div');
    holder.innerHTML = html;

    for (const br of holder.querySelectorAll('br')) {
      const range = document.createRange();
      range.setStartAfter(br);
      range.setEnd(holder, holder.childNodes.length);
      if (!range.toString().trim().startsWith('*')) continue;

      const noteHolder = document.createElement('div');
      noteHolder.append(range.extractContents());
      br.remove();
      this.#trimMarkupEdges(holder);
      return { value: holder.innerHTML, note: noteHolder.innerHTML };
    }

    return { value: html, note: '' };
  }

  /**
   * Collapses runs of `<br>` into a single break so worksheet values keep the
   * designed line rhythm instead of opening blank lines mid-cell.
   * @param {Element} node
   */
  #collapseLineBreaks(node) {
    node.querySelectorAll('br').forEach((br) => {
      let previous = br.previousSibling;
      while (previous && previous.nodeType === Node.TEXT_NODE && previous.textContent.trim() === '') {
        previous = previous.previousSibling;
      }
      if (previous && previous.nodeType === Node.ELEMENT_NODE && previous.tagName === 'BR') {
        br.remove();
      }
    });
  }

  /**
   * Removes blank text nodes and `<br>` elements from both ends of a node.
   * @param {Element} node
   */
  #trimMarkupEdges(node) {
    const isBlankEdge = (child) =>
      (child.nodeType === Node.TEXT_NODE && child.textContent.trim() === '') ||
      (child.nodeType === Node.ELEMENT_NODE && child.tagName === 'BR');

    while (node.firstChild && isBlankEdge(node.firstChild)) node.firstChild.remove();
    while (node.lastChild && isBlankEdge(node.lastChild)) node.lastChild.remove();
  }

  /**
   * Treat JSON placeholders such as "-" as missing spec data.
   * @param {unknown} value
   * @returns {boolean}
   */
  #isEmptyValue(value) {
    const text = this.#normalizeText(value);
    if (text === '') return true;
    return /^[-–—/]+$/.test(text);
  }

  #normalizeText(value) {
    const node = document.createElement('div');
    node.innerHTML = String(value ?? '');
    return (node.textContent || '').replace(/\s+/g, ' ').trim();
  }

  #setStatus(message) {
    if (!this.#statusEl) return;
    this.#statusEl.hidden = !message;
    this.#statusEl.textContent = message;
  }

  /**
   * Distance from the viewport top that is already occupied when pinned.
   * The header group's sticky sections are measured where they actually pin
   * (their sticky `top` plus height): the header sits at `top: -1px`, so the
   * --sticky-area-height token overshoots by a pixel and lets content show
   * through above the bar. The token remains the fallback when no header
   * section is sticky. Any other bar pinned at the top, such as an anchor tab
   * section, opts in with `data-sticky-offset-source`. A bar that also carries
   * `data-sticky-follows-header` pins directly under the header, so the toolbar
   * clears both; other bars pin at the top and overlap it. A hide-on-scroll
   * header that is currently hidden no longer occupies the top of the viewport.
   * @returns {number}
   */
  #stickyOffset() {
    let headerOffset = 0;
    // Shopify adds this class to every section rendered from the header group.
    document.querySelectorAll('.shopify-section-group-header-group').forEach((section) => {
      if (!this.#headerVisible && section.querySelector('store-header, store-header-v2')) return;
      const style = getComputedStyle(section);
      if (style.position !== 'sticky' && style.position !== 'fixed') return;
      const top = parseFloat(style.top);
      const pinnedBottom = (Number.isFinite(top) ? top : 0) + section.getBoundingClientRect().height;
      headerOffset = Math.max(headerOffset, pinnedBottom);
    });

    if (!headerOffset && this.#headerVisible) {
      const probe = document.createElement('div');
      probe.setAttribute('aria-hidden', 'true');
      probe.style.cssText =
        'position:fixed;top:var(--sticky-area-height,0px);visibility:hidden;pointer-events:none;';
      document.body.append(probe);
      const tokenOffset = probe.getBoundingClientRect().top;
      probe.remove();
      headerOffset = Number.isFinite(tokenOffset) ? tokenOffset : 0;
    }

    let barBottom = 0;
    document.querySelectorAll('[data-sticky-offset-source]').forEach((bar) => {
      if (this.contains(bar)) return;
      const height = bar.getBoundingClientRect().height;
      barBottom = Math.max(barBottom, this.#pinnedTop(bar, headerOffset) + height);
    });

    // Round down so a fractional header height overlaps the bar by a subpixel
    // instead of leaving a hairline gap.
    const offset = Math.max(0, Math.floor(Math.max(headerOffset, barBottom)));
    this.style.setProperty('--product-compare-sticky-offset', `${offset}px`);
    return offset;
  }

  /**
   * Where a pinned bar's top edge settles. The bar's nearest sticky or fixed box
   * decides it: an anchor tab that follows the header sits at `calc(visible
   * header height - 1px)`, which is -1px once the header has hidden, so assuming
   * 0 would leave a one pixel gap under it. A `top` that is mid-transition
   * reports where it started, so the end value of that transition wins. Without
   * a usable `top`, a bar that follows the header falls back to the header offset.
   * @param {Element} bar
   * @param {number} headerOffset
   * @returns {number}
   */
  #pinnedTop(bar, headerOffset) {
    let box = bar;
    while (box && !['sticky', 'fixed'].includes(getComputedStyle(box).position)) box = box.parentElement;
    if (box) {
      const transition = box.getAnimations().find((animation) => animation.transitionProperty === 'top');
      const keyframes = transition?.effect?.getKeyframes?.() ?? [];
      const settled = parseFloat(keyframes[keyframes.length - 1]?.top ?? getComputedStyle(box).top);
      if (Number.isFinite(settled)) return settled;
    }
    return bar.hasAttribute('data-sticky-follows-header') ? headerOffset : 0;
  }

  /**
   * Move the single columns node between its in-flow slot and the pinned bar.
   * The cards keep their measured height while the columns are away, so the
   * slot they leave behind never collapses and spec rows never jump. That slot
   * is already scrolled past when pinning starts, so the held space is not
   * visible.
   * @param {boolean} isStuck
   */
  #syncStuck(isStuck) {
    const toolbar = this.#toolbarEl;
    const cards = this.#cardsEl;
    const columns = this.#columnsEl;
    if (!toolbar || !cards || !columns) return;

    const wasStuck = toolbar.hasAttribute('data-stuck');
    if (isStuck === wasStuck) return;

    if (isStuck) {
      cards.style.height = `${cards.getBoundingClientRect().height}px`;
      toolbar.append(columns);
      toolbar.hidden = false;
      toolbar.setAttribute('data-stuck', '');
      this.#measureStuckBar(toolbar);
      return;
    }

    toolbar.removeAttribute('data-stuck');
    toolbar.hidden = true;
    cards.append(columns);
    cards.style.height = '';
    // Reparenting keeps an open picker anchored to a bar that is now hidden.
    this.querySelectorAll('[data-product-picker][open]').forEach((picker) => {
      picker.open = false;
    });
  }

  #clearStuck() {
    this.#syncStuck(false);
  }

  /**
   * The bar is pinned while the cards are scrolled past and the specs are not.
   * Without the second condition the bar would hang over the footer link and the
   * content after the section until the whole section ends.
   */
  #applyStuck() {
    this.#syncStuck(this.#cardsPassed && !this.#specsPassed);
  }

  /**
   * The bar must leave once the last spec row has gone behind it, so its height
   * moves the line the specs end is tested against. The height is only known once
   * the bar is shown, so the specs observer is rebuilt when it changes.
   * @param {Element} toolbar
   */
  #measureStuckBar(toolbar) {
    const height = Math.ceil(toolbar.getBoundingClientRect().height);
    if (height === this.#stuckBarHeight) return;
    this.#stuckBarHeight = height;
    this.#observeSpecsEnd();
  }

  /**
   * An observer only reports when a target starts or stops intersecting. A jump
   * that carries it from above the viewport to below it (a tab link back to the
   * top of the page) never intersects, so no report comes and the bar would keep
   * its old state. Once scrolling settles, read where both markers really are.
   */
  #onScroll = () => {
    clearTimeout(this.#settleTimer);
    this.#settleTimer = setTimeout(() => {
      this.#settleTimer = 0;
      const sentinel = this.querySelector('[data-sticky-sentinel]');
      if (!sentinel || !this.#stickyObserver) return;
      this.#cardsPassed = sentinel.getBoundingClientRect().bottom < this.#pinOffset;
      const specs = this.#specsEl;
      this.#specsPassed = Boolean(specs) && specs.getBoundingClientRect().bottom < this.#pinOffset + this.#stuckBarHeight;
      this.#applyStuck();
    }, 120);
  };

  #observeSpecsEnd() {
    this.#specsObserver?.disconnect();
    this.#specsObserver = null;
    const specs = this.#specsEl;
    if (!specs) return;

    this.#specsObserver = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry) return;
        // As with the cards sentinel, only "above the line" counts as passed.
        const line = entry.rootBounds ? entry.rootBounds.top : 0;
        this.#specsPassed = !entry.isIntersecting && entry.boundingClientRect.bottom <= line;
        this.#applyStuck();
      },
      {
        threshold: 0,
        rootMargin: `-${this.#pinOffset + this.#stuckBarHeight}px 0px 0px 0px`,
      }
    );
    this.#specsObserver.observe(specs);
  }

  #observeSticky() {
    this.#stickyObserver?.disconnect();
    this.#specsObserver?.disconnect();
    const sentinel = this.querySelector('[data-sticky-sentinel]');
    const toolbar = this.#toolbarEl;
    if (!sentinel || !toolbar) return;

    const offset = this.#stickyOffset();
    this.#pinOffset = offset;
    this.#stickyObserver = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry) return;
        // `isIntersecting` is false both above and below the viewport, so the
        // sentinel's position decides: only a sentinel scrolled past the pin
        // line means the cards have left.
        const pinLine = entry.rootBounds ? entry.rootBounds.top : 0;
        this.#cardsPassed = !entry.isIntersecting && entry.boundingClientRect.top <= pinLine;
        this.#applyStuck();
      },
      {
        threshold: 0,
        rootMargin: `-${offset}px 0px 0px 0px`,
      }
    );
    this.#stickyObserver.observe(sentinel);
    this.#observeSpecsEnd();
  }

  #onClick = (event) => {
    const disabledPicker = event.target.closest('[data-product-picker][data-picker-disabled]');
    if (disabledPicker && this.contains(disabledPicker)) {
      event.preventDefault();
      return;
    }

    const learnMore = event.target.closest('[data-financing-learn-more]');
    if (learnMore && this.contains(learnMore)) {
      event.preventDefault();
      this.#openFinancing(learnMore);
      return;
    }

    const option = event.target.closest('[data-select-product]');
    if (!option || !this.contains(option)) return;

    event.preventDefault();
    const nextIndex = Number(option.dataset.selectProduct);
    const column = option.closest('[data-product-index]');
    const currentIndex = Number(column?.dataset.productIndex);
    if (!Number.isInteger(nextIndex) || !Number.isInteger(currentIndex)) return;
    if (nextIndex === currentIndex) return;

    const slot = this.#visibleIndexes.indexOf(currentIndex);
    if (slot === -1) return;
    if (this.#lockFirst && slot === 0) return;

    // Models already shown elsewhere are not selectable, so slots never swap.
    if (this.#visibleIndexes.includes(nextIndex)) return;

    this.#visibleIndexes[slot] = nextIndex;
    column?.querySelector('[data-product-picker]')?.removeAttribute('open');
    this.#sync();
  };

  #onToggle = (event) => {
    const picker = event.target;
    if (!(picker instanceof HTMLDetailsElement)) return;
    if (!picker.matches('[data-product-picker]') || !this.contains(picker)) return;
    if (!picker.open) return;

    this.querySelectorAll('[data-product-picker][open]').forEach((openPicker) => {
      if (openPicker !== picker) openPicker.open = false;
    });
  };

  #onPointerDown = (event) => {
    const openPicker = this.querySelector('[data-product-picker][open]');
    if (!openPicker) return;
    if (openPicker.contains(event.target)) return;
    openPicker.open = false;
  };

  #onKeyDown = (event) => {
    if (event.key !== 'Escape') return;
    this.querySelectorAll('[data-product-picker][open]').forEach((picker) => {
      picker.open = false;
    });
  };

  #onFilterChange = () => {
    this.#applyFilter();
  };

  // The header slides away on scroll down and back on scroll up; follow it so
  // the pinned bar neither leaves a gap nor slips under the header.
  #onHeaderVisibilityChange = (event) => {
    this.#headerVisible = event.detail?.visible !== false;
    this.#observeSticky();
  };

  #onBreakpointChange = () => {
    this.#clearStuck();
    this.#observeSticky();
    this.#sync();
  };

  // The pinned tab bar and header can change height across widths. Height-only
  // resizes (mobile address bar collapsing mid-scroll) are ignored so a pinned
  // bar does not unpin and flicker while scrolling.
  #onResize = () => {
    if (window.innerWidth === this.#viewportWidth) return;
    this.#viewportWidth = window.innerWidth;
    if (this.#resizeFrame) cancelAnimationFrame(this.#resizeFrame);
    this.#resizeFrame = requestAnimationFrame(() => {
      this.#resizeFrame = 0;
      // The held card height is measured per viewport, so release before
      // re-observing and let the observer pin again against fresh geometry.
      this.#clearStuck();
      this.#observeSticky();
      this.#queueWrapCheck();
    });
  };

  #onBlockSelect = (event) => {
    const column = event.target.closest('[data-product-index]');
    if (!column || !this.contains(column)) return;
    const index = Number(column.dataset.productIndex);
    if (!this.#isAvailable(index) || this.#visibleIndexes.includes(index)) return;
    if (!this.#visibleIndexes.length) return;
    this.#visibleIndexes[this.#visibleIndexes.length - 1] = index;
    this.#sync();
  };
}

if (!customElements.get('product-compare-models')) {
  customElements.define('product-compare-models', ProductCompareModels);
}
